// AddressDetail.tsx
import React, { useState } from 'react';
import { X, Check, AlertTriangle, MapPin, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import type { AddressVerification } from '../../types';

interface AddressDetailProps {
  item: AddressVerification;
  onClose: () => void;
  onVerify: (id: number, approved: boolean, reason?: string) => Promise<void>;
}

export const AddressDetail: React.FC<AddressDetailProps> = ({ item, onClose, onVerify }) => {
  const [showConfirm, setShowConfirm] = useState(false);
  const [decision, setDecision] = useState<'approve' | 'reject' | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const getFullAddress = () => {
    const parts = [
      item.house_number,
      item.address_line_1,
      item.address_line_2,
      item.city__name
    ].filter(Boolean);
    return parts.join(', ');
  };

  const googleMapsUrl = `https://www.google.com/maps?q=${item.latitude},${item.longitude}`;
  const hasCoordinates = item.latitude && item.longitude;

  const handleDecision = async () => {
    try {
      await onVerify(item.id, decision === 'approve', rejectReason);
      toast.success(`Address ${decision === 'approve' ? 'approved' : 'rejected'}`);
      onClose();
    } catch (error) {
      toast.error('Failed to process verification');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg max-w-4xl w-full p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Address Verification</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <X size={20} />
            </button>
          </div>

          {/* User Info */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <MapPin size={20} className="text-blue-600" />
              </div>
              <div className="ml-4">
                <h4 className="font-medium">{item.user__first_name} {item.user__last_name}</h4>
                <p className="text-sm text-gray-500">{item.user__email}</p>
                <p className="text-xs text-gray-400 mt-1">
                  Submitted: {new Date(item.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          {/* Address and Map Side by Side */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* Written Address */}
            <div className="border rounded-lg p-4">
              <h4 className="font-medium text-gray-700 mb-2">Submitted Address</h4>
              <p className="text-gray-600">{getFullAddress()}</p>
              {item.delivery_note && (
                <p className="text-sm text-gray-500 mt-2">
                  Note: {item.delivery_note}
                </p>
              )}
            </div>

            {/* Map View */}
            <div className="border rounded-lg overflow-hidden">
              {hasCoordinates ? (
                <>
                  <div className="h-48 bg-gray-100">
                    <iframe
                      title="Address location"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${item.longitude},${item.latitude}&layer=mapnik&marker=${item.latitude},${item.longitude}`}
                    />
                  </div>
                  <div className="p-2 bg-gray-50 flex justify-between items-center">
                    <span className="text-xs text-gray-500">
                      📍 {item.latitude}, {item.longitude}
                    </span>
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      Open in Google Maps
                      <ExternalLink size={12} className="ml-1" />
                    </a>
                  </div>
                </>
              ) : (
                <div className="h-48 bg-gray-100 flex items-center justify-center">
                  <p className="text-gray-500">No coordinates available</p>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {!showConfirm ? (
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setDecision('reject');
                  setShowConfirm(true);
                }}
                className="px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50"
              >
                <X size={18} className="inline mr-2" />
                Reject Address
              </button>
              <button
                onClick={() => {
                  setDecision('approve');
                  setShowConfirm(true);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                <Check size={18} className="inline mr-2" />
                Approve Address
              </button>
            </div>
          ) : (
            <div className="border-t pt-4">
              {decision === 'reject' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg"
                    placeholder="Explain why this address was rejected..."
                    autoFocus
                  />
                </div>
              )}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDecision}
                  disabled={decision === 'reject' && !rejectReason.trim()}
                  className={`px-4 py-2 rounded-lg text-white ${
                    decision === 'approve'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-red-600 hover:bg-red-700'
                  } disabled:opacity-50`}
                >
                  Confirm {decision === 'approve' ? 'Approval' : 'Rejection'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};